import { ConsultCta } from "@/components/ConsultCta";
import { CheckCircle2, XCircle, AlertTriangle, Info, ExternalLink, Zap, Search, LineChart, ImageIcon, Link2, FileText, Gauge } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { TID } from "@/constants/testIds";
import { useLang } from "@/context/LangContext";
import { TechContactButton } from "@/components/analysis/TechContactButton";

const ScoreCard = ({ label, score, testId, icon: Icon, tone = "default" }) => {
  const grade = score >= 90 ? "A" : score >= 75 ? "B" : score >= 60 ? "C" : score >= 40 ? "D" : "F";
  const color =
    score >= 75 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <div
      data-testid={testId}
      className="p-6 border border-[#e4e4e7] bg-white hover:border-[#09090b] transition-colors duration-200"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#71717a] font-mono">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </div>
        <span className="font-mono text-xs text-[#a1a1aa]">/100</span>
      </div>
      <div className="flex items-end justify-between">
        <div className="font-display text-6xl leading-none" style={{ color: tone === "primary" ? "#E0262C" : "#09090b" }}>
          {score}
        </div>
        <div className="font-display text-3xl leading-none" style={{ color }}>
          {grade}
        </div>
      </div>
      <div className="mt-4 h-1 bg-[#f4f4f5] relative overflow-hidden">
        <div className="absolute inset-y-0 left-0" style={{ width: `${score}%`, background: color }} />
      </div>
    </div>
  );
};

const CHECK_EN = {
  "Titolo pagina": ["Page title", "Title should be 10 to 70 characters long."],
  "Meta description": ["Meta description", "Description should be 50 to 160 characters long."],
  "Tag H1 unico": ["Single H1 tag", "Each page should have exactly one H1 tag."],
  "Meta viewport": ["Meta viewport", "Required for mobile compatibility."],
  "Canonical URL": ["Canonical URL", "Helps avoid duplicate content."],
  "Open Graph": ["Open Graph", "Improves sharing on social networks."],
  "Twitter Card": ["Twitter Card", "Improves the preview on X/Twitter."],
  "HTTPS": ["HTTPS", "The page must use HTTPS."],
  "Alt sulle immagini": ["Image alt text", null],
  "Dati strutturati (schema.org)": ["Structured data (schema.org)", "Add JSON-LD for rich snippets."],
  "Contenuto testuale": ["Text content", "Pages with at least 300 words rank better."],
  "Robots meta": ["Robots meta", "The page is set to noindex!"],
  "Google Analytics": ["Google Analytics", "Track traffic with GA4."],
  "Meta Pixel": ["Meta Pixel", "Useful for Facebook/Instagram retargeting."],
};
const VALUE_EN = [[/caratteri/, "characters"], [/trovati/, "found"], [/^assente$/, "missing"], [/^completo$/, "complete"], [/^incompleto$/, "incomplete"],
  [/^sì$/, "yes"], [/blocchi/, "blocks"], [/parole/, "words"], [/^installato$/, "installed"]];

const localizeCheck = (c, lang) => {
  if (lang !== "en" || !CHECK_EN[c.name]) return c;
  const [name, hint] = CHECK_EN[c.name];
  return { ...c, name, hint: hint || c.hint.replace("immagini senza attributo alt.", "images without alt attribute."),
    value: VALUE_EN.reduce((v, [re, en]) => v.replace(re, en), String(c.value ?? "")) };
};

const CheckRow = ({ check: raw, t, lang }) => {
  const check = localizeCheck(raw, lang);
  const icon = check.passed ? (
    <CheckCircle2 className="w-4 h-4 text-[#10b981]" strokeWidth={2.5} />
  ) : check.severity === "critical" ? (
    <XCircle className="w-4 h-4 text-[#ef4444]" strokeWidth={2.5} />
  ) : check.severity === "warning" ? (
    <AlertTriangle className="w-4 h-4 text-[#f59e0b]" strokeWidth={2.5} />
  ) : (
    <Info className="w-4 h-4 text-[#71717a]" strokeWidth={2.5} />
  );

  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#f4f4f5] last:border-b-0">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-sm font-medium text-[#09090b] truncate">{check.name}</div>
          <div className="text-[11px] font-mono text-[#71717a] whitespace-nowrap">{check.value}</div>
        </div>
        {!check.passed && (
          <div className="text-xs text-[#71717a] mt-1">{check.hint}</div>
        )}
      </div>
    </div>
  );
};

const impactColor = (impact) => {
  if (impact === "high") return { bg: "#E0262C", text: "#ffffff" };
  if (impact === "medium") return { bg: "#f59e0b", text: "#09090b" };
  return { bg: "#e4e4e7", text: "#09090b" };
};

export const SeoReport = ({ data, t }) => {
  const { lang } = useLang();
  if (!data) return null;
  const { analysis, suggestions } = data;
  const { scores, meta, headings, images, links, analytics, word_count, fetched_ms, size_kb } = analysis;

  return (
    <section data-testid={TID.reportSection} className="w-full">
      {/* URL bar */}
      <div className="flex items-center gap-2 mb-6 text-sm">
        <ExternalLink className="w-3.5 h-3.5 text-[#71717a]" />
        <a href={analysis.url} target="_blank" rel="noreferrer" className="font-mono text-[#E0262C] hover:underline truncate">
          {analysis.url}
        </a>
      </div>

      {/* Scores grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#e4e4e7] mb-10 divide-x divide-[#e4e4e7]">
        <ScoreCard label={t("overall")} score={scores.overall} testId={TID.scoreOverall} icon={Gauge} tone="primary" />
        <ScoreCard label={t("seo")} score={scores.seo} testId={TID.scoreSeo} icon={Search} />
        <ScoreCard label={t("speed")} score={scores.speed} testId={TID.scoreSpeed} icon={Zap} />
        <ScoreCard label={t("analytics")} score={scores.analytics} testId={TID.scoreAnalytics} icon={LineChart} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        {/* AI suggestions */}
        <div className="lg:col-span-2 border border-[#e4e4e7] p-6" data-testid={TID.suggestionsList}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-xl">{t("aiSuggestions")}</h3>
            <Badge className="bg-[#E0262C] hover:bg-[#E0262C] text-white rounded-sm font-mono text-[10px]">CLAUDE</Badge>
          </div>
          <div className="space-y-4">
            {suggestions?.length ? (
              suggestions.map((s, i) => {
                const c = impactColor(s.impact);
                return (
                  <div key={i} className="flex gap-4 p-4 bg-[#fafafa] border border-[#f4f4f5]">
                    <div className="font-display text-xl text-[#a1a1aa] w-8">{String(i + 1).padStart(2, "0")}</div>
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-3 mb-1">
                        <div className="font-semibold text-[#09090b]">{s.title}</div>
                        <span
                          className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-sm"
                          style={{ background: c.bg, color: c.text }}
                        >
                          {t(s.impact) || s.impact}
                        </span>
                      </div>
                      <div className="text-sm text-[#52525b]">{s.description}</div>
                      <TechContactButton data={data} s={s} i={i} />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-[#71717a]">—</div>
            )}
          </div>
        </div>

        {/* Meta tags */}
        <div className="border border-[#e4e4e7] p-6">
          <h3 className="font-display text-xl mb-5">{t("metaTags")}</h3>
          <div className="space-y-3 text-sm">
            <MetaRow label={t("title")} value={meta.title || "—"} />
            <MetaRow label={t("description")} value={meta.description || "—"} />
            <MetaRow label={t("canonical")} value={meta.canonical || "—"} mono />
            <MetaRow label={t("viewport")} value={meta.viewport || "—"} mono />
            <MetaRow label={t("robots")} value={meta.robots || "index, follow"} mono />
            <MetaRow label={t("ogTitle")} value={meta.og_title || "—"} />
          </div>
        </div>
      </div>

      {/* Technical checks + stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 border border-[#e4e4e7] p-6">
          <h3 className="font-display text-xl mb-4">{t("checks")}</h3>
          <div>
            {analysis.checks.map((c, i) => (
              <CheckRow key={i} check={c} t={t} lang={lang} />
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <StatBlock title={t("stats")}>
            <StatRow icon={FileText} label={t("words")} value={word_count} />
            <StatRow icon={ImageIcon} label={t("images")} value={`${images.total} (${images.missing_alt} ${t("missingAlt")})`} />
            <StatRow icon={Link2} label={t("internalLinks")} value={links.internal} />
            <StatRow icon={Link2} label={t("externalLinks")} value={links.external} />
            <StatRow icon={Zap} label={t("loadTime")} value={`${fetched_ms} ms`} />
            <StatRow icon={Gauge} label={t("pageSize")} value={`${size_kb} KB`} />
          </StatBlock>

          <StatBlock title={t("trackers")}>
            {Object.entries(analytics).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-[#f4f4f5] last:border-0">
                <span className="text-sm capitalize">{k.replace(/_/g, " ")}</span>
                {v ? (
                  <span className="text-[10px] font-mono uppercase text-[#10b981]">● {t("installed")}</span>
                ) : (
                  <span className="text-[10px] font-mono uppercase text-[#a1a1aa]">○ {t("notFound")}</span>
                )}
              </div>
            ))}
          </StatBlock>
        </div>
      </div>
      <ConsultCta id={data.id} url={analysis.url} score={scores?.overall} />
    </section>
  );
};

const MetaRow = ({ label, value, mono = false }) => (
  <div>
    <div className="text-[10px] uppercase font-mono text-[#71717a] tracking-widest mb-1">{label}</div>
    <div className={`text-sm break-words ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
  </div>
);

const StatBlock = ({ title, children }) => (
  <div className="border border-[#e4e4e7] p-6">
    <h4 className="text-[10px] uppercase font-mono tracking-widest text-[#71717a] mb-4">{title}</h4>
    {children}
  </div>
);

const StatRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-[#f4f4f5] last:border-0">
    <div className="flex items-center gap-2 text-sm text-[#52525b]">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <span className="font-mono text-sm">{value}</span>
  </div>
);
